import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CanvasJSReact from "@canvasjs/react-charts";

import Logo from "../images/logo1.png";
import styles from "../css/landing.module.css";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPeopleGroup,
  faUserPlus,
  faUserGroup,
  faClipboardUser,
  faMoneyBill,
  faMoneyBill1,
  faReceipt,
  faUser,
  faHouse,
  faArrowRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function Goasohome() {

  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);

  // =============================
  // FETCH MEMBERS
  // =============================

  useEffect(() => {

    axios
      .get("https://appserver.cemkharistemple.com/goasoallmembers")
      .then((res) => setUsers(res.data))
      .catch((err) => console.error(err));

  }, []);

  const totalMembers = users.length;

  // =============================
  // FETCH ATTENDANCE
  // =============================

  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = async () => {

    try {

      const res = await fetch(
        "https://appserver.cemkharistemple.com/goasoattendance"
      );

      const result = await res.json();

      // ===== ORIGINAL ATTENDANCE LINE CHART =====

      const formatted = result.map((item) => ({
        label: item.service_date,
        y: parseFloat(item.total_count),
      }));

      setAttendanceData(formatted);

      processAttendanceCharts(result);

    } catch (err) {

      console.error(err);

    }

  };

  // =============================
  // PROCESS ATTENDANCE TABLE
  // =============================

  const processAttendanceCharts = (data) => {

    const yearly = {};
    const monthly = {};

    data.forEach((item) => {

      const date = new Date(item.service_date);

      const year = date.getFullYear();

      const month = date.toLocaleString("default", { month: "short" });

      if (!yearly[year]) yearly[year] = { male: 0, female: 0 };

      if (!monthly[`${year}-${month}`])
        monthly[`${year}-${month}`] = { male: 0, female: 0 };

      yearly[year].male += Number(item.male || 0);
      yearly[year].female += Number(item.female || 0);

      monthly[`${year}-${month}`].male += Number(item.male || 0);
      monthly[`${year}-${month}`].female += Number(item.female || 0);

    });

    const sortedYears = Object.keys(yearly).sort();

    setYearlyData([
      {
        type: "line",
        showInLegend: true,
        legendText: "Male",
        dataPoints: sortedYears.map((y) => ({
          label: y,
          y: yearly[y].male,
        })),
      },
      {
        type: "line",
        showInLegend: true,
        legendText: "Female",
        dataPoints: sortedYears.map((y) => ({
          label: y,
          y: yearly[y].female,
        })),
      },
    ]);

    const sortedMonths = Object.keys(monthly).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    setMonthlyData([
      {
        type: "column",
        showInLegend: true,
        legendText: "Male",
        dataPoints: sortedMonths.map((m) => ({
          label: m,
          y: monthly[m].male,
        })),
      },
      {
        type: "column",
        showInLegend: true,
        legendText: "Female",
        dataPoints: sortedMonths.map((m) => ({
          label: m,
          y: monthly[m].female,
        })),
      },
    ]);

  };

  // =============================
  // CHART OPTIONS
  // =============================

  const attendanceOptions = {
    animationEnabled: true,
    title: { text: "Attendance Overview" },
    axisY: { title: "Total Count" },
    axisX: { labelAngle: -45 },
    data: [
      {
        type: "line",
        dataPoints: attendanceData,
      },
    ],
  };

  const yearlyOptions = {
    animationEnabled: true,
    title: { text: "Male vs Female (Yearly Trend)" },
    axisY: { title: "Total Attendance" },
    data: yearlyData,
  };

  const monthlyOptions = {
    animationEnabled: true,
    title: { text: "Male vs Female (Monthly Bar)" },
    axisY: { title: "Total Attendance" },
    axisX: { labelAngle: -45 },
    data: monthlyData,
  };

  // ================= FINANCE (TITHE) TREND =================
  const [titheData, setTitheData] = useState([]);
  const [financeYear, setFinanceYear] = useState("all");

  useEffect(() => {
    axios
      .get("https://appserver.cemkharistemple.com/alltithe")
      .then((res) => setTitheData(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error(err));
  }, []);

  const financeYears = useMemo(() => {
    const set = new Set();
    titheData.forEach((t) => {
      const d = new Date(t.date);
      if (!isNaN(d)) set.add(d.getFullYear());
    });
    return Array.from(set).sort();
  }, [titheData]);

  const financeChart = useMemo(() => {
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    if (financeYear === "all") {
      const map = {};
      titheData.forEach((t) => {
        const d = new Date(t.date);
        if (isNaN(d)) return;
        const y = d.getFullYear();
        map[y] = (map[y] || 0) + Number(t.amount || 0);
      });
      const years = Object.keys(map).sort();
      return {
        title: "Finance Trend (Yearly)",
        points: years.map((y) => ({ label: y, y: map[y] })),
      };
    }
    const arr = new Array(12).fill(0);
    titheData.forEach((t) => {
      const d = new Date(t.date);
      if (isNaN(d)) return;
      if (String(d.getFullYear()) !== String(financeYear)) return;
      arr[d.getMonth()] += Number(t.amount || 0);
    });
    return {
      title: "Finance Trend (" + financeYear + ")",
      points: months.map((m, i) => ({ label: m, y: arr[i] })),
    };
  }, [titheData, financeYear]);

  const financeOptions = {
    animationEnabled: true,
    title: { text: financeChart.title },
    axisY: { title: "Amount (GHS)", prefix: "₵" },
    axisX: { labelAngle: 0 },
    data: [{ type: "column", dataPoints: financeChart.points }],
  };

  const handleLogout = () => {

    localStorage.removeItem("accessToken");
    localStorage.removeItem("token");

    window.location.href = "/";

  };

  return (

    <div className={styles.container}>

      {/* HEADER */}

      <Topbar title="Goaso Temple" subtitle="Members, attendance & finance overview" />

      {/* SIDEBAR */}

      <Sidebar variant="goaso" />

      {/* MAIN */}

      <main className={styles.main}>

        <section className={styles.stats}>

          <div className={styles.statCard}>

            <div className={styles.statTop}>Total Members</div>

            <div className={styles.statValue}>
              {totalMembers}
            </div>

            <div className={styles.statHint}>
              Active members
            </div>

          </div>

        </section>

        <section className={styles.section}>

          <div className={styles.sectionHeader}>

            <h2 className={styles.sectionTitle}>
              Attendance Trend
            </h2>

            <p className={styles.sectionSub}>
              Weekly service attendance performance
            </p>

          </div>

          <div className={styles.statCard}>
            <CanvasJSChart options={attendanceOptions} />
          </div>

        </section>

        <section className={styles.section}>

          <div className={styles.statCard}>
            <CanvasJSChart options={yearlyOptions} />
          </div>

        </section>

        <section className={styles.section}>

          <div className={styles.statCard}>
            <CanvasJSChart options={monthlyOptions} />
          </div>

        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeaderRow}>
            <div className={styles.sectionHeader} style={{ marginBottom: 0 }}>
              <h2 className={styles.sectionTitle}>Finance Trend</h2>
              <p className={styles.sectionSub}>Tithe collections over time</p>
            </div>
            <select
              className={styles.filterSelect}
              value={financeYear}
              onChange={(e) => setFinanceYear(e.target.value)}
            >
              <option value="all">All years</option>
              {financeYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.statCard}>
            {titheData.length ? (
              <CanvasJSChart options={financeOptions} />
            ) : (
              <div className={styles.chartEmpty}>No finance data available yet.</div>
            )}
          </div>
        </section>

      </main>

    </div>

  );
}

export default Goasohome;